import { useAutoAnimate } from '@formkit/auto-animate/react'
import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { useQuery } from '@tanstack/react-query'
import Button from 'ext-ui/components/Button/Button'
import { Location } from 'history'
import React, { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { CreateVirtualCardScreen } from 'src/app/CreateVirtualCardForm/CreateVirtualCardScreen'
import PhysicalCardIssuanceModal, {
  type PhysicalCardIssuanceIntent,
} from 'src/component-modal/PhysicalCardIssuanceModal/PhysicalCardIssuanceModal'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgAttachmentFile from 'src/components/Svgs/SvgAttachmentFile'
import SvgBillPayNew from 'src/components/Svgs/SvgBillPayNew'
import SvgBudgetCreateLabel from 'src/components/Svgs/SvgBudgetCreateLabel'
import SvgBudgetRequestLabel from 'src/components/Svgs/SvgBudgetRequestLabel'
import SvgCardCreateLabel from 'src/components/Svgs/SvgCardCreateLabel'
import SvgCardRequestLabel from 'src/components/Svgs/SvgCardRequestLabel'
import SvgExpense from 'src/components/Svgs/SvgExpense'
import SvgPayInvoice from 'src/components/Svgs/SvgPayInvoice'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { OrganizationRoles } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import { useIsUserOrglessRCNUser } from 'src/hooks/useOrglessUserStatus'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import {
  actionUnavailableUrl,
  createBillPayUrl,
  createBudgetUrl,
  createVirtualCardUrl,
  paymentsUrl,
  requestBudgetUrl,
  requestVirtualCardUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import {
  isOutOfPocketExpensesVisible,
  orgOutOfPocketExpensesQueries,
} from 'src/queries/organizations/outOfPocketExpenses/outOfPocketExpenses.queries'
import { useQueryOrganizationBillPayEnabled } from 'src/queries/organizations/virtualCardTypes/virtualCardTypes.queries'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import {
  createSetIsCreateExpenseReportPopupOpen,
  createSetIsSetupOrganizationPopupOpen,
} from 'src/redux/actions/app.actions'
import { LocationState } from 'src/redux/createRootReducer'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectUserExplicitOrganizationIdFromState,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectLocationFromState,
} from 'src/redux/selectors/router.selectors'
import { selectBrandFeaturesFromState } from 'src/redux/selectors/session.selectors'
import variables from 'src/style/variables.json'
import { canManageBillPay } from 'src/utils/billPayAccess'
import {
  isBillPayM3PaymentsEnabled,
  isPhysicalCardIssuanceEnabled,
} from 'src/utils/featureFlags'
import { css } from 'styled-components'

/**
 * One event per feature with an `action` property, per src/analytics/AGENTS.md's
 * current standard — not the `noun - verb` shape of the older events around it
 * in this file, which that doc lists as legacy and explicitly grandfathers
 * rather than invites. Two entries of the same feature separated by one
 * dimension is exactly the funnel case the standard is for.
 */
const PHYSICAL_CARD_ISSUANCE_EVENT = 'physical card issuance'

export type CreateNewDropdownOption =
  | CreateNewDropdownOptionAriaSource
  | CreateNewDropdownOptionAriaLabel

type CreateNewDropdownSection = {
  id: string
  label: string
  options: CreateNewDropdownOption[]
}

interface CreateNewDropdownOptionShared {
  id?: string
  label: React.ReactNode
  image?: React.ReactNode
  to?: string | Location<LocationState>
  onClick?: () => void
  grayed?: boolean
  key?: string | number
  subtitle?: React.ReactNode
}

interface CreateNewDropdownOptionAriaSource
  extends CreateNewDropdownOptionShared {
  ariaLabelSource: string
  ariaLabel?: undefined
}

interface CreateNewDropdownOptionAriaLabel
  extends CreateNewDropdownOptionShared {
  ariaLabel: string
  ariaLabelSource?: undefined
}

interface CreateNewDropdownProps {
  paddingTop?: boolean
  rightOffset?: number
  isScrolled?: boolean
  isSecondaryCta?: boolean
}

const CreateNewDropdown: React.FC<CreateNewDropdownProps> = (props) => {
  const translate = useTranslate()

  const buttonRef: React.MutableRefObject<HTMLDivElement | null> = useRef(null)
  const { analytics } = useAnalyticsContext()

  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const { data: hasOutOfPocketExpenses } = useQuery(
    orgOutOfPocketExpensesQueries.enabled(organization?.id),
  )
  const outOfPocketExpensesEnabled = isOutOfPocketExpensesVisible(
    organization,
    hasOutOfPocketExpenses,
  )

  // The four counts branched on below, plus CreditCardsUserOrViewerMeCount,
  // whose loaded flag gated the menu contents even though its value is never
  // read here (legacy relied on other surfaces fetching it).
  const { results: statisticsResults } = useQueryStatistics({
    statisticsKeys: [
      StatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCount,
      StatisticsKeys.CreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount,
      StatisticsKeys.CreditCardsCanIssueVirtualCardsCount,
      StatisticsKeys.CreditCardsSourceBudgetsEnabledCanIssueBudgetsCount,
      StatisticsKeys.CreditCardsUserOrViewerMeCount,
    ] as const,
  })
  const creditCardsActiveCanIssueVirtualCardsCount =
    statisticsResults[StatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCount]
      .data ?? 0
  const creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount =
    statisticsResults[
      StatisticsKeys.CreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount
    ].data ?? 0
  const creditCardsCanIssueVirtualCardsCount =
    statisticsResults[StatisticsKeys.CreditCardsCanIssueVirtualCardsCount]
      .data ?? 0
  const creditCardsSourceBudgetsEnabledCanIssueBudgetsCount =
    statisticsResults[
      StatisticsKeys.CreditCardsSourceBudgetsEnabledCanIssueBudgetsCount
    ].data ?? 0
  const allNecessaryStatisticsLoaded =
    statisticsResults[StatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCount]
      .isSuccess &&
    statisticsResults[
      StatisticsKeys.CreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount
    ].isSuccess &&
    statisticsResults[StatisticsKeys.CreditCardsCanIssueVirtualCardsCount]
      .isSuccess &&
    statisticsResults[
      StatisticsKeys.CreditCardsSourceBudgetsEnabledCanIssueBudgetsCount
    ].isSuccess &&
    statisticsResults[StatisticsKeys.CreditCardsUserOrViewerMeCount].isSuccess
  const isUserOrglessRCNUser = useIsUserOrglessRCNUser()
  const currentLocation = useSelector(selectLocationFromState)
  const user = useCurrentUser()
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const isGuestUser =
    user?.organizationRole === OrganizationRoles.guest && showGuestRole
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const { CREATE_BILL_PAY_CARD: enableCreateBillPayFeature } =
    useQueryUserFeatures([UserFeature.CREATE_BILL_PAY_CARD]).data || {}
  const { billPayEnabled } = useSelector(selectBrandFeaturesFromState)

  const { data: organizationBillPayEnabled } =
    useQueryOrganizationBillPayEnabled({
      orgId: user?.organizationId,
      enabled: !!enableCreateBillPayFeature && billPayEnabled,
    })

  const showBillPayForOrganization = organizationBillPayEnabled === true

  const enableBillPay =
    enableCreateBillPayFeature && billPayEnabled && showBillPayForOrganization

  // ACH gate — must mirror PaymentsPage exactly, including the explicit-org
  // selector as the org-id source, or the menu can offer ACH to a user the
  // destination page then denies.
  const remoteConfig = useSelector(selectFeatureFlaggerFromState)
  const explicitOrganizationId = useSelector(
    selectUserExplicitOrganizationIdFromState,
  )
  const billPayPaymentsEnabled = isBillPayM3PaymentsEnabled(
    remoteConfig,
    explicitOrganizationId,
  )
  // enabled-gated: no dark /features request while the M3 flag is off.
  const { data: billPayUserFeatures } = useQueryUserFeatures(
    [UserFeature.VIEW_PAYABLES, UserFeature.ORGANIZATION_USER_STATISTICS],
    { enabled: billPayPaymentsEnabled },
  )
  const canViewPayables =
    billPayUserFeatures?.[UserFeature.VIEW_PAYABLES] ?? false
  const achBillPayEnabled =
    billPayPaymentsEnabled &&
    canManageBillPay({
      hasOrgAdminPermissions: user?.hasOrgAdminPermissions,
      organizationRole: user?.organizationRole,
      orgUserStatisticsEnabled:
        billPayUserFeatures?.[UserFeature.ORGANIZATION_USER_STATISTICS],
    }) &&
    canViewPayables

  const [dropdownRef] = useAutoAnimate({ duration: 100, easing: 'ease-in' })

  const dispatch = useBackgroundDispatch()

  const [dropdownOpen, setDropdownOpen] = useState(false)
  // Which physical-card placeholder is open, or undefined for closed (EX-40738).
  // Local state rather than a route or a redux popup: the modal is inert — it
  // submits nothing and owns no flow — so nothing about it is worth making
  // linkable or restoring across a reload.
  const [physicalCardIssuanceIntent, setPhysicalCardIssuanceIntent] =
    useState<PhysicalCardIssuanceIntent>()
  const [isHovered, setIsHovered] = useState(false)

  const handleHoverButtonAnalytics = (): void => {
    analytics?.track('create new button', {
      screen: calculatedBackgroundLocation.pathname,
    })
  }

  const listItem = (
    option: CreateNewDropdownOption,
    i: number,
  ): React.ReactElement => (
    <HoverableLink
      id={option.id}
      testId={option.id}
      css={css`
        display: flex;
        white-space: wrap;
        ${getFontCss(
          14,
          themeConstants.colors.cta,
          styleConstants.fontFamilies.medium,
        )};
        align-items: start;
      `}
      key={i}
      to={option.to}
      {...(option.ariaLabelSource !== undefined
        ? { ariaLabelSource: option.ariaLabelSource }
        : { ariaLabel: option.ariaLabel })}
      onClick={(): void => {
        option.onClick && option.onClick()
      }}
    >
      {option.image && (
        <div
          css={css`
            display: flex;
            margin-right: 5px;
          `}
        >
          {option.image}
        </div>
      )}
      <div>
        <div
          css={css`
            ${!option.subtitle &&
            `
            margin-top: 5px;
          `}
            &:hover {
              text-decoration: underline;
            }
          `}
        >
          {option.label}
        </div>
        {option.subtitle && (
          <div
            css={css`
              ${getFontCss(
                10,
                styleConstants.colors.gray_dark,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            {option.subtitle}
          </div>
        )}
      </div>
    </HoverableLink>
  )

  const createVcAvailableOption: CreateNewDropdownOption[] | undefined =
    creditCardsCanIssueVirtualCardsCount > 0 &&
    creditCardsActiveCanIssueVirtualCardsCount > 0
      ? [
          {
            id: 'createVcnAvailNavTop',
            label: translate('Create virtual cards'),
            to: createVirtualCardUrl(),
            onClick: (): void => {
              analytics?.track('create virtual card flyout - click-in')
            },
            ariaLabelSource: 'Create virtual cards',
            image: <SvgCardCreateLabel />,
          },
        ]
      : undefined
  const createVcUnavailableOption: CreateNewDropdownOption[] | undefined =
    creditCardsCanIssueVirtualCardsCount > 0 &&
    creditCardsActiveCanIssueVirtualCardsCount === 0
      ? [
          {
            id: 'createVcnUnavailNavTop',
            label: translate('Create virtual cards'),
            to: actionUnavailableUrl(),
            grayed: true,
            ariaLabelSource: 'Create virtual cards',
            image: <SvgCardCreateLabel />,
          },
        ]
      : undefined
  const requestBudgetAvaiableOption: CreateNewDropdownOption[] | undefined = [
    {
      id: 'requestBudgetAvailNavTop',
      label: translate('Request a budget'),
      to: requestBudgetUrl(),
      onClick: (): void => {
        analytics?.track('request budget')
      },
      ariaLabelSource: 'Request a budget',
      subtitle: translate('Delegate creation permissions'),
      image: <SvgBudgetRequestLabel />,
    },
  ]
  const createBudgetAvailableOption: CreateNewDropdownOption[] | undefined =
    creditCardsSourceBudgetsEnabledCanIssueBudgetsCount > 0 &&
    creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount > 0
      ? isUserOrglessRCNUser
        ? [
            {
              id: 'createBudgetAvailNavTop',
              label: translate('Create a budget'),
              to: currentLocation.pathname,
              onClick: (): void => {
                dispatch(
                  createSetIsSetupOrganizationPopupOpen(
                    true,
                    createBudgetUrl(),
                  ),
                )
              },
              ariaLabelSource: 'Create a budget',
              subtitle: translate('Delegate creation permissions'),
              image: <SvgBudgetCreateLabel />,
            },
          ]
        : [
            {
              id: 'createBudgetAvailNavTop',
              label: translate('Create a budget'),
              to: createBudgetUrl(),
              onClick: (): void => {
                // New Universal Mixpanel Event
                analytics?.track('create budget flyout - click-in')
              },
              ariaLabelSource: 'Create a budget',
              subtitle: translate('Get access to create virtual cards'),
              image: <SvgBudgetCreateLabel />,
            },
            {
              id: 'requestBudgetAvailNavTop',
              label: translate('Request a budget'),
              to: requestBudgetUrl(),
              onClick: (): void => {
                // New Universal Mixpanel Event
                analytics?.track('request budget flyout - click-in')
              },
              ariaLabelSource: 'Request a budget',
              subtitle: translate('Delegate creation permissions'),
              image: <SvgBudgetRequestLabel />,
            },
          ]
      : undefined
  const createBudgetUnavailableOption: CreateNewDropdownOption[] | undefined =
    creditCardsSourceBudgetsEnabledCanIssueBudgetsCount > 0 &&
    creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount === 0
      ? [
          {
            id: 'createBudgetUnavailNavTop',
            label: translate('Create a budget'),
            to: actionUnavailableUrl(),
            grayed: true,
            ariaLabelSource: 'Create a budget',
            subtitle: translate('Delegate creation permissions'),
            image: <SvgBudgetCreateLabel />,
          },
        ]
      : undefined
  const createInBulkAvailableOption: CreateNewDropdownOption[] | undefined =
    creditCardsCanIssueVirtualCardsCount > 0 &&
    creditCardsActiveCanIssueVirtualCardsCount > 0
      ? [
          {
            id: 'createInBulkAvailNavTop',
            label: translate('Create in bulk (CSV)'),
            to: {
              pathname: createVirtualCardUrl(),
              search: '',
              state: { screen: CreateVirtualCardScreen.Multiple },
              hash: '',
            },
            onClick: (): void => {
              analytics?.track('create in bulk (csv) flyout - click-in')
            },
            ariaLabelSource: 'Create in bulk (CSV)',
            image: <SvgAttachmentFile />,
          },
        ]
      : undefined
  const createInBulkUnavailableOption: CreateNewDropdownOption[] | undefined =
    creditCardsCanIssueVirtualCardsCount > 0 &&
    creditCardsActiveCanIssueVirtualCardsCount === 0
      ? [
          {
            id: 'createInBulkUnavailNavTop',
            label: translate('Create in bulk (CSV)'),
            to: actionUnavailableUrl(),
            grayed: true,
            ariaLabelSource: 'Create in bulk (CSV)',
            image: <SvgAttachmentFile />,
          },
        ]
      : undefined

  const createInvoiceAvailableOption: CreateNewDropdownOption[] | undefined =
    creditCardsCanIssueVirtualCardsCount > 0 &&
    creditCardsActiveCanIssueVirtualCardsCount > 0
      ? [
          {
            id: 'createInvoiceAvailNavTop',
            label: translate('Pay by virtual card'),
            to: createBillPayUrl(),
            ariaLabelSource: 'Pay by virtual card',
            image: <SvgPayInvoice />,
            subtitle: translate('Create a virtual card for one invoice'),
          },
        ]
      : undefined
  const createInvoiceUnavailableOption: CreateNewDropdownOption[] | undefined =
    creditCardsCanIssueVirtualCardsCount > 0 &&
    creditCardsActiveCanIssueVirtualCardsCount === 0
      ? [
          {
            id: 'createInvoiceUnavailNavTop',
            label: translate('Pay by virtual card'),
            to: actionUnavailableUrl(),
            grayed: true,
            ariaLabelSource: 'Pay by virtual card',
            image: <SvgPayInvoice />,
            subtitle: translate('Create a virtual card for one invoice'),
          },
        ]
      : undefined
  const createBulkInvoiceAvailableOption:
    | CreateNewDropdownOption[]
    | undefined =
    creditCardsCanIssueVirtualCardsCount > 0 &&
    creditCardsActiveCanIssueVirtualCardsCount > 0
      ? [
          {
            id: 'createBulkInvoiceAvailNavTop',
            label: translate('Pay in bulk by virtual card'),
            to: {
              pathname: createBillPayUrl(),
              search: '',
              state: { screen: CreateVirtualCardScreen.MultipleBillPay },
              hash: '',
            },
            ariaLabelSource: 'Pay in bulk by virtual card',
            image: <SvgPayInvoice />,
            subtitle: translate('Upload multiple invoices via CSV'),
          },
        ]
      : undefined
  const createBulkInvoiceUnavailableOption:
    | CreateNewDropdownOption[]
    | undefined =
    creditCardsCanIssueVirtualCardsCount > 0 &&
    creditCardsActiveCanIssueVirtualCardsCount === 0
      ? [
          {
            id: 'createBulkInvoiceUnavailNavTop',
            label: translate('Pay in bulk by virtual card'),
            to: actionUnavailableUrl(),
            grayed: true,
            ariaLabelSource: 'Pay in bulk by virtual card',
            image: <SvgPayInvoice />,
            subtitle: translate('Upload multiple invoices via CSV'),
          },
        ]
      : undefined
  // No grayed variant: /payments owns the first-time experience, so the entry
  // has no preconditions beyond the access gate.
  const achInvoiceOption: CreateNewDropdownOption[] = [
    {
      id: 'payInvoiceAchNavTop',
      label: translate('Pay by ACH'),
      to: paymentsUrl(),
      onClick: (): void => {
        analytics?.track('pay invoice by ach - click-in')
      },
      ariaLabelSource: 'Pay by ACH',
      image: <SvgBillPayNew />,
      subtitle: translate('Pay from your bank account'),
    },
  ]
  const createExpenseReportCreateOption: CreateNewDropdownOption[] | undefined =
    [
      {
        id: 'createExpenseReportCreateNavTop',
        label: translate('Create Reimbursement'),
        subtitle: translate('Submit reimbursable expenses'),
        onClick: (): void => {
          dispatch(createSetIsCreateExpenseReportPopupOpen(true))
          analytics?.track('create OOPE flyout - click-in')
        },
        ariaLabelSource: 'Create Reimbursement',
        image: <SvgExpense />,
      },
    ]

  // EX-40738 — the two physical-card entries. Client flag OR the org's server-side
  // card-management enrollment, read off `orgFeatureFlags` — the SAME query this
  // component already runs for `guestRoleEnabled`, so the org check costs no
  // additional request. Org-scoped on purpose: the `/capabilities` contract the
  // sibling card-management gates use is card-scoped, and this menu is global.
  const physicalCardIssuanceEnabled = isPhysicalCardIssuanceEnabled(
    remoteConfig,
    orgFeatureFlags?.physicalCardManagementEnabled,
  )
  // Issuing is Admin/Owner-only — `hasOrgAdminPermissions` is exactly
  // ADMIN + OWNER within an explicit org. Card Managers and below get the
  // request entry below instead, which routes to an Admin/Owner (the Card
  // Approver). Fails closed on an unresolved user.
  const canIssuePhysicalCard = user?.hasOrgAdminPermissions === true
  // Both entries need an organization to be meaningful, so both are built on the
  // same predicate: `hasOrgAdminPermissions` already requires `isInExplicitOrg`,
  // and requesting needs it because the request ROUTES to an Admin/Owner — there
  // is nobody to route to without an org.
  //
  // Without this, an orgless RCN user with the client flag on would get exactly
  // one entry (Request — `hasOrgAdminPermissions` is false for them) leading to a
  // modal telling them to ask an Admin or Owner in an organization they do not
  // have. Their org verdict can never grant either: `useQueryOrganizationFeatureFlags`
  // is `enabled: !!orgId`-gated off, so `orgFeatureFlags` stays undefined.
  //
  // Checked directly rather than via `useIsUserOrglessRCNUser` (used below for
  // budgets): that hook also requires `CreditCardsSourceCount > 0`, so an orgless
  // user with no sources would slip through it. Fails closed on an unresolved user.
  const userIsInExplicitOrg = user?.isInExplicitOrg === true
  const issuePhysicalCardOption: CreateNewDropdownOption[] | undefined =
    physicalCardIssuanceEnabled && canIssuePhysicalCard
      ? [
          {
            id: 'issuePhysicalCardNavTop',
            label: translate('Issue a physical card'),
            subtitle: translate('Order directly — ships to the cardholder'),
            onClick: (): void => {
              analytics?.track(PHYSICAL_CARD_ISSUANCE_EVENT, {
                action: 'issue',
              })
              setPhysicalCardIssuanceIntent('issue')
            },
            ariaLabelSource: 'Issue a physical card',
            image: <SvgCardCreateLabel />,
          },
        ]
      : undefined
  // Offered to everyone the menu is open to, Admins/Owners included — matching
  // the design. An Admin/Owner seeing both is not a contradiction: the request
  // entry is how they raise one on someone else's behalf when the direct order
  // is not what they want. The role gate is on ISSUE, per the ticket.
  const requestPhysicalCardOption: CreateNewDropdownOption[] | undefined =
    physicalCardIssuanceEnabled && userIsInExplicitOrg
      ? [
          {
            id: 'requestPhysicalCardNavTop',
            label: translate('Request a physical card'),
            subtitle: translate('Routes to an Admin/Owner for approval'),
            onClick: (): void => {
              analytics?.track(PHYSICAL_CARD_ISSUANCE_EVENT, {
                action: 'request',
              })
              setPhysicalCardIssuanceIntent('request')
            },
            ariaLabelSource: 'Request a physical card',
            image: <SvgCardRequestLabel />,
          },
        ]
      : undefined

  const requestVcnOption: CreateNewDropdownOption[] | undefined = !isGuestUser
    ? [
        {
          id: 'requestVcnNavTop',
          label: translate('Request a virtual card'),
          to: requestVirtualCardUrl(),
          onClick: (): void => {
            // New Mixpanel Event
            analytics?.track('request virtual card flyout - click-in')
          },
          ariaLabelSource: 'Request a virtual card',
          image: <SvgCardRequestLabel />,
        },
      ]
    : undefined

  const physicalCardOptions: CreateNewDropdownSection = {
    id: 'physicalCards',
    label: 'Physical Cards',
    options: [
      ...(issuePhysicalCardOption || []),
      ...(requestPhysicalCardOption || []),
    ],
  }

  const vcnOptions: CreateNewDropdownSection = {
    id: 'virtualCards',
    label: 'Virtual Cards',
    options: [
      ...(createVcAvailableOption || createVcUnavailableOption || []),
      ...(createInBulkAvailableOption || createInBulkUnavailableOption || []),
      ...(requestVcnOption || []),
    ],
  }

  const budgetOptions: CreateNewDropdownSection = {
    id: 'budgets',
    label: 'Budgets',
    options: [
      ...(createBudgetAvailableOption ||
        createBudgetUnavailableOption ||
        requestBudgetAvaiableOption ||
        []),
    ],
  }

  const billPayOptions: CreateNewDropdownSection = {
    id: 'billPay',
    label: 'Bill Pay',
    options: [
      // VCN options derive only from card counts, so gate them on enableBillPay
      // here — otherwise an ACH-only org with issuable cards would leak the
      // legacy VCN entries it isn't entitled to.
      ...(enableBillPay
        ? [
            ...(createInvoiceAvailableOption ||
              createInvoiceUnavailableOption ||
              []),
            ...(createBulkInvoiceAvailableOption ||
              createBulkInvoiceUnavailableOption ||
              []),
          ]
        : []),
      ...(achBillPayEnabled ? achInvoiceOption : []),
    ],
  }

  const expenseOptions: CreateNewDropdownSection = {
    id: 'expenseReports',
    label: 'Reimbursement Reports',
    options: [...createExpenseReportCreateOption],
  }

  const standardOptions = [
    // First, above Virtual Cards, per the design. Guests are excluded like every
    // other section.
    //
    // The emptiness check is LOAD-BEARING, not defensive. The `.map()` below
    // derives each section's top border from its ARRAY index (`i === 0` → no
    // border), while the `section.options?.length > 0` guard only suppresses
    // rendering. So an empty-but-present section still consumes index 0 and
    // pushes the first VISIBLE section to index 1, where it picks up a
    // `border-top` — a stray divider just inside the popup's own border, for
    // every non-guest user while the flag is off. Keeping the section out of
    // the array until it has something in it makes `i` the rendered index again.
    !isGuestUser && physicalCardOptions.options.length > 0
      ? physicalCardOptions
      : undefined,
    !isGuestUser ? vcnOptions : undefined,
    !isGuestUser ? budgetOptions : undefined,
    (enableBillPay || achBillPayEnabled) && !isGuestUser
      ? billPayOptions
      : undefined,
    outOfPocketExpensesEnabled ? expenseOptions : undefined,
  ].filter(
    (section): section is CreateNewDropdownSection =>
      typeof section !== 'undefined',
  )

  return (
    <>
      {(!isGuestUser || outOfPocketExpensesEnabled) && (
        <div
          ref={buttonRef}
          css={css`
            position: relative;
            display: flex;
            align-items: flex-end;
            flex-direction: column;

            ${props.paddingTop &&
            getBreakpointDown(breakpointSizes.XSM)(css`
              margin-top: 12px;
            `)}
          `}
          onMouseEnter={(): void => {
            setIsHovered(true)
            setDropdownOpen(true)
            handleHoverButtonAnalytics()
          }}
          onMouseLeave={(): void => {
            setIsHovered(false)
            setDropdownOpen(false)
          }}
          onBlur={(): undefined => {
            return
          }}
          onFocus={(): undefined => {
            return
          }}
        >
          {
            <div
              id={'topNavCreateNew'}
              data-testid={'topNavCreateNew'}
              data-isopen={dropdownOpen}
            >
              {props.isSecondaryCta ? (
                <HoverableLink
                  css={`
                    padding: 0px ${variables.units['base-unit-8']}px;
                  `}
                  ariaLabel="Create New"
                  onClick={(): void => {
                    if (!isHovered) {
                      setDropdownOpen(!dropdownOpen)
                    }
                  }}
                >
                  {translate('Create New +')}
                </HoverableLink>
              ) : (
                <Button
                  css={`
                    padding: 0px ${variables.units['base-unit-8']}px;
                  `}
                  type="button"
                  variant="primary"
                  onClick={(): void => {
                    if (!isHovered) {
                      setDropdownOpen(!dropdownOpen)
                    }
                  }}
                >
                  {translate('Create New +')}
                </Button>
              )}
            </div>
          }
          <div ref={dropdownRef}>
            {dropdownOpen && (
              <div
                css={css`
                  z-index: 1;
                  position: absolute;
                  right: 0px;
                  border-radius: 10px;
                  display: flex;
                  flex-direction: column;
                  border: 1px solid ${styleConstants.colors.gray_medium};
                  box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.15);
                  width: 250px;
                  background-color: ${styleConstants.colors.white};
                `}
              >
                {
                  // all necessary stats are loaded
                  allNecessaryStatisticsLoaded &&
                    standardOptions !== undefined &&
                    standardOptions.map(
                      (section, i) =>
                        section !== undefined &&
                        section.options?.length > 0 && (
                          <div
                            key={`CreateNewDropdownSection${i}`}
                            // The index the top border is derived from, exposed
                            // so it can be asserted. The border itself is set
                            // through the `css` prop, which resolves to nothing
                            // in jsdom (no class, no stylesheet), so a rendered
                            // divider is otherwise invisible to tests — which is
                            // exactly how an empty section silently consuming
                            // index 0 shipped a stray rule above the first
                            // visible section.
                            data-section-index={i}
                            data-section-id={section.id}
                            css={css`
                              border-top: ${i === 0
                                ? `none`
                                : `1px solid ${styleConstants.colors.gray_medium}`};
                              padding-bottom: 10px;
                              padding-top: 10px;
                            `}
                          >
                            <div
                              css={css`
                                ${getFontCss(
                                  14,
                                  styleConstants.colors.gray_darkest,
                                  styleConstants.fontFamilies.medium,
                                )};
                                padding: 4px 15px;
                              `}
                            >
                              {translate(section.label)}
                            </div>
                            {section.options.map((item, j) => (
                              <div
                                key={`CreateNewDropdownSection${i}-Option${j}`}
                                css={css`
                                  padding: 4px 15px;
                                  ${item.grayed &&
                                  `
                            opacity: 0.5;
                            cursor: default;
                          `}
                                `}
                              >
                                {listItem(item, i)}
                              </div>
                            ))}
                          </div>
                        ),
                    )
                }
              </div>
            )}
          </div>
        </div>
      )}
      {/*
        Rendered OUTSIDE the hover container above: that div closes the dropdown
        on mouseleave, and the modal must survive the pointer leaving it. It
        portals anyway, but keeping it out of that subtree also keeps it clear of
        the dropdown's open/close state entirely.
      */}
      <PhysicalCardIssuanceModal
        intent={physicalCardIssuanceIntent}
        onClose={(): void => setPhysicalCardIssuanceIntent(undefined)}
      />
    </>
  )
}

export default CreateNewDropdown
